import React, { useCallback, useMemo, useState } from 'react'
import clsx from 'clsx'
import { Box, Typography } from '@mui/material'
import { ActivityLogButton, Button } from 'src/components/atoms'
import CenteringLoading from 'src/components/molecules/CenteringLoading'
import EditCalculationMethod from 'src/components/molecules/EditCalculationMethod'
import Modal from 'src/components/molecules/Modal'
import { Option } from 'src/components/molecules/SelectWithOption'
import SmallGuide from 'src/components/molecules/SmallGuide'
import CalculationTable, {
  CalculationTableProps,
} from 'src/components/organisms/CalculationTable'
import {
  AdminAdminSalesReportManagerEditorSalesReportManagerEditorStoreManagerInOperationCenterRoles,
  isOneOfRoles,
} from 'src/domain/role'
// FIXME: tabContentStyle.tsがTenantDetail配下にあるのに他のページから参照されているのは良くない
// 共通のスタイルとして適切な場所に移動するか、このコンポーネント専用のスタイルを作成する必要がある
import tabContentStyle from 'src/pages/TenantDetail/tabContentStyle'
import {
  CalculationMethod,
  CalculationRoundingTypeEnum,
  RoleEnum,
} from 'src/slices/services/api'
import { formatDateWithTimeAndWeekday } from 'src/utils/date'
import styles from './styles.module.scss'

export type CalculationTabProps = {
  role?: RoleEnum
  lastUpdatedAt?: string
  isLoading: boolean
  calculationMethod?: CalculationMethod
  roundingTypeOptions: Option[]
  precisionOptions: Option[]
  onClickEditButton: () => void
  onClickEditCalculationMethod: (
    roundingType: CalculationRoundingTypeEnum,
    precision: number
  ) => void
  onClickResetButton: () => void
  onClickHistoryButton?: () => void
} & Partial<Pick<CalculationTableProps, 'className' | 'columns' | 'records'>>

const CalculationTab: React.StyledFC<CalculationTabProps> = ({
  className,
  isLoading,
  calculationMethod,
  roundingTypeOptions,
  precisionOptions,
  role,
  lastUpdatedAt,
  onClickEditButton,
  onClickEditCalculationMethod,
  onClickResetButton,
  onClickHistoryButton,
  columns = [],
  records = [],
}: CalculationTabProps) => {
  const tabClasses = tabContentStyle()
  const [showResetModal, setShowResetModal] = useState<boolean>(false)

  const showSwitchByRole = useCallback((): boolean => {
    return isOneOfRoles(
      AdminAdminSalesReportManagerEditorSalesReportManagerEditorStoreManagerInOperationCenterRoles,
      role
    )
  }, [role])

  const formattedLastUpdatedAt = useMemo(() => {
    return lastUpdatedAt ? formatDateWithTimeAndWeekday(lastUpdatedAt) : ''
  }, [lastUpdatedAt])

  const canShowHistoryButton =
    Boolean(onClickHistoryButton) && isOneOfRoles([RoleEnum.Admin], role)

  const handleClickResetButton = () => {
    setShowResetModal(true)
  }

  const handleClickResetAgree = () => {
    setShowResetModal(false)
    onClickResetButton()
  }

  const handleClickResetDisagree = () => {
    setShowResetModal(false)
  }

  const renderContent = (renderRecords: CalculationTableProps['records']) => {
    return renderRecords.length > 0 ? (
      <>
        <Box
          className={clsx(className, tabClasses.calcMethodWithLastUpdatedAt)}
        >
          <EditCalculationMethod
            calculationMethod={calculationMethod}
            roundingTypeOptions={roundingTypeOptions}
            precisionOptions={precisionOptions}
            role={role}
            onClickEditCalculationMethod={onClickEditCalculationMethod}
          />
          <Box className={styles.buttonContainer}>
            {lastUpdatedAt && (
              <Box className={tabClasses.lastUpdatedAt}>
                <Typography variant="subtitle2">最終更新日時</Typography>
                <Typography>
                  {formattedLastUpdatedAt.toLocaleString()}
                </Typography>
                {canShowHistoryButton && (
                  <ActivityLogButton onClick={onClickHistoryButton} />
                )}
              </Box>
            )}
            {!lastUpdatedAt && canShowHistoryButton && (
              <ActivityLogButton onClick={onClickHistoryButton} />
            )}
            {showSwitchByRole() && (
              <Box className={tabClasses.buttonWrapper}>
                <Button
                  title="算出ロジックをリセット"
                  color="secondary"
                  size="large"
                  onClick={handleClickResetButton}
                />
                <Button
                  title="算出ロジックを編集"
                  color="primary"
                  size="large"
                  onClick={onClickEditButton}
                />
              </Box>
            )}
          </Box>
        </Box>
        <CalculationTable
          className={tabClasses.table}
          columns={columns}
          records={renderRecords}
        />
        <Modal
          open={showResetModal}
          title={`算出ロジックと対応表をリセットします。\nよろしいですか？`}
          agreeButtonTitle="リセットする"
          disagreeButtonTitle="キャンセル"
          onClickAgree={handleClickResetAgree}
          onClickDisagree={handleClickResetDisagree}
        />
      </>
    ) : (
      <SmallGuide
        icon="pray"
        message="利用準備がされていません"
        description="まずは登録して利用開始の準備をしましょう"
        buttonTitle={!showSwitchByRole() ? '' : '設定'}
        onClick={onClickEditButton}
      />
    )
  }

  return (
    <Box
      className={clsx(
        tabClasses.root,
        records.length === 0 && tabClasses.guideRoot
      )}
    >
      {isLoading ? <CenteringLoading /> : renderContent(records)}
    </Box>
  )
}

export default CalculationTab
